import React, { useState, useEffect } from 'react';
import { deliveryAreaApi, DeliveryArea, Province } from '../services/deliveryAreaApi';
import { Search, RefreshCw, Save, CheckCircle, XCircle } from 'lucide-react';

interface NeighborhoodsManagementTabProps {
  provinces: Province[];
  selectedProvince: Province | null;
}

interface NeighborhoodEdit {
  id: string;
  deliveryFee: number;
  estimatedDuration: string;
  isActive: boolean;
}

export default function NeighborhoodsManagementTab({ 
  provinces, 
  selectedProvince 
}: NeighborhoodsManagementTabProps) {
  const [neighborhoods, setNeighborhoods] = useState<DeliveryArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkPrice, setBulkPrice] = useState('');
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<Set<string>>(new Set());
  const [editedNeighborhoods, setEditedNeighborhoods] = useState<Map<string, NeighborhoodEdit>>(new Map());
  
  // Filters
  const [filters, setFilters] = useState({
    province: selectedProvince?.name || '',
    district: '',
    minFee: '',
    maxFee: '',
    isActive: 'all' as 'all' | 'true' | 'false',
  });

  // District list for dropdown
  const [districts, setDistricts] = useState<string[]>([]);

  useEffect(() => {
    if (selectedProvince) {
      setFilters(prev => ({ ...prev, province: selectedProvince.name }));
    }
  }, [selectedProvince]);

  useEffect(() => {
    // Update district list when province changes
    if (filters.province) {
      const province = provinces.find(p => p.name === filters.province);
      if (province) {
        const districtNames = province.districts.map((d: any) => d.name);
        setDistricts(districtNames);
      }
    }
  }, [filters.province, provinces]);

  const loadNeighborhoods = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (filters.province) params.province = filters.province;
      if (filters.district) params.district = filters.district;
      if (filters.minFee) params.minFee = Number(filters.minFee);
      if (filters.maxFee) params.maxFee = Number(filters.maxFee);
      if (filters.isActive !== 'all') params.isActive = filters.isActive === 'true';

      const data = await deliveryAreaApi.getAllNeighborhoods(params);
      setNeighborhoods(data.neighborhoods);
      setEditedNeighborhoods(new Map());
      setSelectedNeighborhoods(new Set());
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedNeighborhoods.size === neighborhoods.length) {
      setSelectedNeighborhoods(new Set());
    } else {
      setSelectedNeighborhoods(new Set(neighborhoods.map(n => n.id)));
    }
  };

  const handleSelectNeighborhood = (id: string) => {
    const newSelected = new Set(selectedNeighborhoods);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNeighborhoods(newSelected);
  };

  const handleEdit = (id: string, field: keyof NeighborhoodEdit, value: any) => {
    const neighborhood = neighborhoods.find(n => n.id === id);
    if (!neighborhood) return;

    const currentEdit = editedNeighborhoods.get(id) || {
      id,
      deliveryFee: neighborhood.deliveryFee,
      estimatedDuration: neighborhood.estimatedDuration,
      isActive: neighborhood.isActive,
    };

    const newEdit = { ...currentEdit, [field]: value };
    const newEdited = new Map(editedNeighborhoods);
    newEdited.set(id, newEdit);
    setEditedNeighborhoods(newEdited);
  };

  const getValidatedBulkPrice = () => {
    if (!bulkPrice.trim()) {
      alert('🔔 Lütfen toplu uygulanacak fiyatı girin');
      return null;
    }

    const deliveryFee = Number(bulkPrice);

    if (!Number.isFinite(deliveryFee) || deliveryFee < 0) {
      alert('❌ Geçerli bir fiyat girin');
      return null;
    }

    return deliveryFee;
  };

  const handleBulkPriceForSelected = async () => {
    const deliveryFee = getValidatedBulkPrice();
    if (deliveryFee === null) return;

    if (selectedNeighborhoods.size === 0) {
      alert('🔔 Önce en az bir mahalle seçin');
      return;
    }

    if (!confirm(`${selectedNeighborhoods.size} seçili mahalle için teslimat ücreti ${deliveryFee} TL olarak güncellenecek. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      setSaving(true);
      const result = await deliveryAreaApi.bulkUpdateNeighborhoods(
        Array.from(selectedNeighborhoods),
        { deliveryFee }
      );
      alert(`✅ ${result.updated} seçili mahallede fiyat güncellendi!`);
      await loadNeighborhoods();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkPriceForDistrict = async () => {
    const deliveryFee = getValidatedBulkPrice();
    if (deliveryFee === null) return;

    if (!filters.province || !filters.district) {
      alert('🔔 İlçe bazlı toplu fiyat güncellemesi için il ve ilçe seçin');
      return;
    }

    if (!confirm(`${filters.province} / ${filters.district} ilçesindeki tüm mahallelerin teslimat ücreti ${deliveryFee} TL olarak güncellenecek. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      setSaving(true);
      const result = await deliveryAreaApi.bulkUpdate(filters.province, filters.district, {
        deliveryFee,
      });
      alert(`✅ ${result.updated} mahallede fiyat güncellendi!`);
      await loadNeighborhoods();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (editedNeighborhoods.size === 0) {
      alert('🔔 Değişiklik yok');
      return;
    }

    if (!confirm(`${editedNeighborhoods.size} mahalle güncellenecek. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      setSaving(true);
      
      const updates: any = {};
      const firstEdit = Array.from(editedNeighborhoods.values())[0];
      
      // Check if all edits have the same changes
      const allSameDeliveryFee = Array.from(editedNeighborhoods.values()).every(
        e => e.deliveryFee === firstEdit.deliveryFee
      );
      const allSameDuration = Array.from(editedNeighborhoods.values()).every(
        e => e.estimatedDuration === firstEdit.estimatedDuration
      );
      const allSameActive = Array.from(editedNeighborhoods.values()).every(
        e => e.isActive === firstEdit.isActive
      );

      // Build updates object
      if (allSameDeliveryFee) updates.deliveryFee = firstEdit.deliveryFee;
      if (allSameDuration) updates.estimatedDuration = firstEdit.estimatedDuration;
      if (allSameActive) updates.isActive = firstEdit.isActive;

      const neighborhoodIds = Array.from(editedNeighborhoods.keys());
      const result = await deliveryAreaApi.bulkUpdateNeighborhoods(neighborhoodIds, updates);
      
      alert(`✅ ${result.updated} mahalle güncellendi!`);
      await loadNeighborhoods();
    } catch (error: any) {
      alert(`❌ Hata: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = editedNeighborhoods.size > 0;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Filtreler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              İl
            </label>
            <select
              value={filters.province}
              onChange={(e) => setFilters({ ...filters, province: e.target.value, district: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tümü</option>
              {provinces.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              İlçe
            </label>
            <select
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!filters.province}
            >
              <option value="">Tümü</option>
              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Ücret (₺)
            </label>
            <input
              type="number"
              value={filters.minFee}
              onChange={(e) => setFilters({ ...filters, minFee: e.target.value })}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Ücret (₺)
            </label>
            <input
              type="number"
              value={filters.maxFee}
              onChange={(e) => setFilters({ ...filters, maxFee: e.target.value })}
              placeholder="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durum
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tümü</option>
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={loadNeighborhoods}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Search size={18} />
            {loading ? 'Aranıyor...' : 'Ara'}
          </button>
          
          <button
            onClick={() => {
              setFilters({
                province: selectedProvince?.name || '',
                district: '',
                minFee: '',
                maxFee: '',
                isActive: 'all',
              });
              setNeighborhoods([]);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            <RefreshCw size={18} />
            Temizle
          </button>
        </div>
      </div>

      {/* Results */}
      {neighborhoods.length > 0 && (
        <>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex-1 max-w-sm">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Toplu Fiyat Güncelle
                </label>
                <input
                  type="number"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder="Örn. 45"
                  min="0"
                  step="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  İlçe seçiliyse tüm ilçeye uygular, seçim yaptıysanız yalnızca seçili mahallelere uygular.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  onClick={handleBulkPriceForDistrict}
                  disabled={saving || !filters.province || !filters.district}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  İlçedeki Tüm Mahallelere Uygula
                </button>
                <button
                  onClick={handleBulkPriceForSelected}
                  disabled={saving || selectedNeighborhoods.size === 0}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  Seçilenlere Uygula
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <strong>{neighborhoods.length}</strong> mahalle bulundu
              {selectedNeighborhoods.size > 0 && (
                <span className="ml-2 text-blue-600">
                  • <strong>{selectedNeighborhoods.size}</strong> seçili
                </span>
              )}
              {hasChanges && (
                <span className="ml-2 text-orange-600">
                  • <strong>{editedNeighborhoods.size}</strong> değişmiş
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {selectedNeighborhoods.size > 0 && (
                <>
                  <button
                    onClick={async () => {
                      if (!confirm(`${selectedNeighborhoods.size} mahalle aktif yapılacak. Onaylıyor musunuz?`)) return;
                      try {
                        setSaving(true);
                        const result = await deliveryAreaApi.bulkUpdateNeighborhoods(
                          Array.from(selectedNeighborhoods),
                          { isActive: true }
                        );
                        alert(`✅ ${result.updated} mahalle aktif yapıldı!`);
                        await loadNeighborhoods();
                      } catch (error: any) {
                        alert(`❌ Hata: ${error.message}`);
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle size={18} />
                    Seçilenleri Aktif Yap
                  </button>

                  <button
                    onClick={async () => {
                      if (!confirm(`${selectedNeighborhoods.size} mahalle pasif yapılacak. Onaylıyor musunuz?`)) return;
                      try {
                        setSaving(true);
                        const result = await deliveryAreaApi.bulkUpdateNeighborhoods(
                          Array.from(selectedNeighborhoods),
                          { isActive: false }
                        );
                        alert(`✅ ${result.updated} mahalle pasif yapıldı!`);
                        await loadNeighborhoods();
                      } catch (error: any) {
                        alert(`❌ Hata: ${error.message}`);
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle size={18} />
                    Seçilenleri Pasif Yap
                  </button>
                </>
              )}

              {hasChanges && (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedNeighborhoods.size === neighborhoods.length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İl
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İlçe
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mahalle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teslimat Ücreti (₺)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Süre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {neighborhoods.map((neighborhood) => {
                    const isEdited = editedNeighborhoods.has(neighborhood.id);
                    const editData = editedNeighborhoods.get(neighborhood.id);
                    const displayData = editData || neighborhood;

                    return (
                      <tr 
                        key={neighborhood.id} 
                        className={`hover:bg-gray-50 ${isEdited ? 'bg-yellow-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedNeighborhoods.has(neighborhood.id)}
                            onChange={() => handleSelectNeighborhood(neighborhood.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {neighborhood.province}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {neighborhood.district}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {neighborhood.neighborhood}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={displayData.deliveryFee}
                            onChange={(e) => handleEdit(neighborhood.id, 'deliveryFee', Number(e.target.value))}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            min="0"
                            step="5"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={displayData.estimatedDuration}
                            onChange={(e) => handleEdit(neighborhood.id, 'estimatedDuration', e.target.value)}
                            className="w-28 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            placeholder="2-4 saat"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEdit(neighborhood.id, 'isActive', !displayData.isActive)}
                            className={`flex items-center gap-1 px-3 py-1 rounded text-sm font-medium transition-colors ${
                              displayData.isActive
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                            }`}
                          >
                            {displayData.isActive ? (
                              <>
                                <CheckCircle size={14} />
                                Aktif
                              </>
                            ) : (
                              <>
                                <XCircle size={14} />
                                Pasif
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && neighborhoods.length === 0 && (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          Filtre uygulayarak mahalle arayın
        </div>
      )}
    </div>
  );
}
